import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarRange, Check, MapPin, Search, Users } from "lucide-react";
import { Avatar } from "../../components/VyberTrenera.jsx";
import { LOKALITY, normalizujLokalitu } from "../../lib/lokality.js";
import { useApiData } from "../../lib/api.js";
import { getCurrentUser } from "../../lib/auth.js";
import { TydenniRozvrh, PropadnutiContext } from "../trener/Dashboard.jsx";
import RozvrhVzor from "../trener/RozvrhVzor.jsx";
import RozvrhVzorPrehled from "../../components/RozvrhVzorPrehled.jsx";
import GradientHeader from "../../components/GradientHeader.jsx";

// Manažerský pohled na rozvrh: výběr trenéra + stejné komponenty jako u trenéra.
const isMgmtPozice = (p) => /head\s*coach|mana[zž]er|manager/i.test(String((p && p.pozice) || "") + " " + String((p && p.role) || ""));

// `vcetneMe` (2. 10. 2026, Láďa): manažer nebo head coach, který sám trénuje (Michal
// Šulc), vidí ve výběru i sebe, ať si může otevřít svůj kalendář a napojit ho do telefonu
// stejně jako trenéři. Ostatní vedení zůstává ve výběru schované.
// `vcetneVedeni` (6. 10. 2026, Láďa): vzorový rozvrh ukazuje i manažera, který trénuje
// (Michal), jinak mu useStickyTrener výběr hned zahodil a mřížka se neukázala.
export function useActiveTrainers({ vcetneMe = false, vcetneVedeni = false } = {}) {
  const { data } = useApiData("/api/hr-list-people?filter=treneri&status=aktivni");
  return useMemo(() => {
    const people = (data && data.people) || [];
    const muj = vcetneMe ? String(getCurrentUser()?.email || "").trim().toLowerCase() : "";
    return people
      .filter((p) => p.is_trener && p.active !== false && (vcetneVedeni || !isMgmtPozice(p) || (muj && String(p.email || "").trim().toLowerCase() === muj)))
      .map((p) => p.display_name)
      .sort((a, b) => a.localeCompare(b, "cs"));
  }, [data, vcetneMe, vcetneVedeni]);
}

// Vybraný trenér se DRŽÍ — je v URL (?trener=…) i v localStorage, takže přežije
// překliknutí na jinou sekci, zpět, refresh i sdílení odkazu (Kristina, 6.8.2026).
const TRENER_KEY = "fw40-mgr-trener";

export function useStickyTrener(trainers) {
  const [params, setParams] = useSearchParams();
  const zUrl = params.get("trener") || "";
  const [trener, setTrenerState] = useState(() => {
    if (zUrl) return zUrl;
    try { return localStorage.getItem(TRENER_KEY) || ""; } catch { return ""; }
  });

  // Po obnovení z localStorage doplň trenéra i do URL, ať je odkaz sdílitelný.
  useEffect(() => {
    if (trener && zUrl !== trener) {
      const next = new URLSearchParams(params);
      next.set("trener", trener);
      setParams(next, { replace: true });
    }
    if (!trener && zUrl) setTrenerState(zUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trener, zUrl]);

  // Když uložený trenér už není v seznamu aktivních, výběr zahodíme.
  useEffect(() => {
    if (trener && trainers.length && !trainers.includes(trener)) setTrener("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainers, trener]);

  const setTrener = (v) => {
    const val = v || "";
    setTrenerState(val);
    try {
      if (val) localStorage.setItem(TRENER_KEY, val);
      else localStorage.removeItem(TRENER_KEY);
    } catch { /* private mode */ }
    const next = new URLSearchParams(params);
    if (val) next.set("trener", val); else next.delete("trener");
    setParams(next, { replace: true });
  };

  return [trener, setTrener];
}

// FW-150 (15. 10. 2026, Láďa, schválená varianta A): místo <select> karty trenérů
// s fotkou nebo iniciálami. Jedna komponenta pro Rozvrh trenérů, Kalendář u manažera
// i kalendář head coache. Víc lokalit = skupiny s nadpisem lokality (u jedné se nadpis
// neukazuje). Na kartě je křestní jméno, při shodě se přidá písmeno příjmení, při
// shodě celého jména celé jméno. Celé jméno je v title a aria-label.
// `info` = { [jméno]: { lokalita, foto } }, nepovinné (bez něj jedna skupina bez fotek).
function popiskyTreneru(jmena) {
  const casti = (j) => String(j || "").trim().split(/\s+/).filter(Boolean);
  const krestni = (j) => casti(j)[0] || String(j || "");
  const pocet = (pole, f) => pole.reduce((m, j) => { const k = f(j).toLowerCase(); m[k] = (m[k] || 0) + 1; return m; }, {});
  const kr = pocet(jmena, krestni);
  const sPis = (j) => { const c = casti(j); return c.length > 1 ? `${c[0]} ${c[c.length - 1][0]}.` : String(j || ""); };
  const sp = pocet(jmena, sPis);
  const out = {};
  for (const j of jmena) {
    if (kr[krestni(j).toLowerCase()] === 1) out[j] = krestni(j);
    else if (sp[sPis(j).toLowerCase()] === 1) out[j] = sPis(j);
    else out[j] = String(j || "");
  }
  return out;
}

export function useTreneriInfo() {
  const { data } = useApiData("/api/hr-list-people?filter=treneri&status=aktivni");
  return useMemo(() => {
    const out = {};
    for (const p of (data && data.people) || []) if (p.display_name) out[p.display_name] = { lokalita: p.lokalita || "", foto: p.foto || "" };
    return out;
  }, [data]);
}

const LOKALITA_FILTR_KEY = "fw40-vyber-trenera-lokalita";
function pocetTreneru(n) { return `${n} ${n === 1 ? "trenér" : n >= 2 && n <= 4 ? "trenéři" : "trenérů"}`; }

export function TrainerSelectBar({ value, onChange, trainers, info = {} }) {
  const popisky = useMemo(() => popiskyTreneru(trainers), [trainers]);
  const [hledat, setHledat] = useState("");
  const [lokFiltr, setLokFiltr] = useState(() => { try { return localStorage.getItem(LOKALITA_FILTR_KEY) || ""; } catch { return ""; } });
  const nastavLok = (v) => { setLokFiltr(v); try { if (v) localStorage.setItem(LOKALITA_FILTR_KEY, v); else localStorage.removeItem(LOKALITA_FILTR_KEY); } catch { /* private mode */ } };
  const lokOf = (t) => normalizujLokalitu(info[t]?.lokalita || "") || "";
  const vsechnySkupiny = useMemo(() => {
    const m = new Map();
    for (const t of trainers) {
      const lok = lokOf(t);
      if (!m.has(lok)) m.set(lok, []);
      m.get(lok).push(t);
    }
    // Lokality v pořadí kanonického seznamu, bez lokality na konec.
    const poradi = (l) => (l ? (LOKALITY.indexOf(l) === -1 ? 50 : LOKALITY.indexOf(l)) : 99);
    return [...m.entries()].sort((x, y) => poradi(x[0]) - poradi(y[0]));
  }, [trainers, info]); // eslint-disable-line react-hooks/exhaustive-deps
  const viceLokalit = vsechnySkupiny.length > 1;
  // Filtr se ukáže, když je víc lokalit nebo hodně trenérů. Uložená lokalita, která
  // v seznamu není, se ignoruje.
  const ukazFiltr = viceLokalit || trainers.length > 8;
  const aktivniLok = viceLokalit && vsechnySkupiny.some(([l]) => l === lokFiltr) ? lokFiltr : "";
  const norm = (x) => String(x || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const q = norm(hledat.trim());
  const skupiny = vsechnySkupiny
    .filter(([l]) => !aktivniLok || l === aktivniLok)
    .map(([l, seznam]) => [l, seznam.filter((t) => !q || norm(t).includes(q))])
    .filter(([, seznam]) => seznam.length > 0);
  return (
    <div className="glass mb-4 !p-3">
      <div className="mb-2 flex items-center gap-2 px-1 text-xs font-bold uppercase tracking-wider text-slate-500"><Users className="h-4 w-4" />Trenér</div>
      {ukazFiltr && (
        <div className="mb-3 flex flex-wrap gap-2">
          <label className="relative min-w-[10rem] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input type="search" value={hledat} onChange={(e) => setHledat(e.target.value)} placeholder="Hledat trenéra" aria-label="Hledat trenéra" className="field w-full !pl-9" />
          </label>
          {viceLokalit && (
            <label className="relative">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <select value={aktivniLok} onChange={(e) => nastavLok(e.target.value)} aria-label="Lokalita" className="field w-auto !pl-9 font-semibold">
                <option value="">Všechny lokality</option>
                {vsechnySkupiny.map(([l]) => <option key={l || "bez"} value={l}>{l || "Bez lokality"}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
      <div className="space-y-3">
        {skupiny.length === 0 && <div className="px-1 py-2 text-sm text-slate-500">Žádný trenér neodpovídá hledání.</div>}
        {skupiny.map(([lok, seznam]) => (
          <div key={lok || "bez"}>
            {viceLokalit && (
              <div className="mb-2 flex items-center gap-2 rounded-xl bg-brand-900 px-3 py-2 text-white">
                <MapPin className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate text-sm font-bold">{lok || "Bez lokality"}</span>
                <span className="shrink-0 rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold">{pocetTreneru(seznam.length)}</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-[repeat(auto-fill,minmax(10.5rem,1fr))]">
              {seznam.map((t) => {
                const vybrany = !!value && String(value).toLowerCase() === String(t).toLowerCase();
                return (
                  <button key={t} type="button" onClick={() => onChange(t)} aria-pressed={vybrany} title={t} aria-label={t}
                    className={`flex min-h-[52px] min-w-0 items-center gap-2.5 rounded-xl px-2.5 text-left transition-colors ${vybrany ? "border-2 border-brand bg-brand-50" : "border border-slate-200 bg-white hover:border-brand/40"}`}>
                    <Avatar name={t} photo={info[t]?.foto} size="sm" />
                    <span className="min-w-0 flex-1 truncate text-[15px] font-bold text-slate-900 demo-sensitive">{popisky[t]}</span>
                    {vybrany && <Check className="h-4 w-4 shrink-0 text-brand" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function pragueDate() {
  return new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" }));
}
function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function mondayOf(offset) {
  const d = pragueDate();
  const dow = (d.getDay() + 6) % 7; // Po = 0
  d.setDate(d.getDate() - dow + offset * 7);
  return isoOf(d);
}

export function ManagerRozvrh() {
  // 15. 10. 2026 (Láďa): i vedení, které trénuje (Michal Šulc).
  const trainers = useActiveTrainers({ vcetneVedeni: true });
  const info = useTreneriInfo();
  const [trener, setTrener] = useStickyTrener(trainers);
  const [weekOffset, setWeekOffset] = useState(0);
  const today = isoOf(pragueDate());
  const weekStart = mondayOf(weekOffset);
  const { data, refetch } = useApiData(trener ? `/api/trener-rozvrh?pilot=${encodeURIComponent(trener)}&weekStart=${weekStart}&days=7` : null);
  // Prefetch sousedních týdnů (±1, v rámci -5..+5) na pozadí — přepnutí týdne je pak okamžité
  // (data jsou už v cache), stejně jako u trenérského dashboardu.
  const prevStart = weekOffset > -5 ? mondayOf(weekOffset - 1) : null;
  const nextStart = weekOffset < 5 ? mondayOf(weekOffset + 1) : null;
  useApiData(trener && prevStart ? `/api/trener-rozvrh?pilot=${encodeURIComponent(trener)}&weekStart=${prevStart}&days=7` : null);
  useApiData(trener && nextStart ? `/api/trener-rozvrh?pilot=${encodeURIComponent(trener)}&weekStart=${nextStart}&days=7` : null);
  return (
    <div>
      <TrainerSelectBar value={trener} onChange={(v) => { setTrener(v); setWeekOffset(0); }} trainers={trainers} info={info} />
      {!trener && <div className="text-sm text-slate-400">Vyber trenéra pro zobrazení týdenního rozvrhu.</div>}
      {trener && (
        <PropadnutiContext.Provider value={{ activeTrener: trener, userRole: getCurrentUser()?.role || "", onMutated: () => refetch?.() }}>
          <TydenniRozvrh
            days={data?.days || []}
            total={data?.total || 0}
            weekOffset={weekOffset}
            onWeekOffsetChange={setWeekOffset}
            weekStart={weekStart}
            today={today}
          />
        </PropadnutiContext.Provider>
      )}
    </div>
  );
}

// 6. 10. 2026 (Láďa, schválený mockup v2): místo <select> karty trenérů s obsazeností,
// souhrn kapacity týmu a filtry. Stránka je pro ownera, manažera i CSM (menu Operations
// u ownera, Trenéři u manažera a CSM), trenér má dál jen svůj /trener/rozvrh-vzor.
export function ManagerRozvrhVzor() {
  const trainers = useActiveTrainers({ vcetneVedeni: true });
  const [trener, setTrener] = useStickyTrener(trainers);
  return (
    <div className="space-y-4">
      <GradientHeader title="Vzorový rozvrh" icon={CalendarRange} subtitle="Obsazenost trenérů za týden a týdenní vzor vybraného trenéra" />
      <RozvrhVzorPrehled trainers={trainers} value={trener} onChange={setTrener} />
      {trener && <RozvrhVzor trenerProp={trener} bezHlavicky />}
    </div>
  );
}
